import { Alert, Box, FormControlLabel, Paper, Switch, Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useEffect, useRef } from "react";
import { downloadBlob } from "../../../components/staffing/downloadScheduleUtils";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import { SystemSortingButtonWithPopup } from "../../../components/UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid/SystemDataGrid";
import type { FailedTab } from "../hooks";
import { EMPLOYEE_MATCH_COLUMN, type Row, useAttemptsFilter } from "../useAttemptsFilter";
import { stripExtension } from "../utils";
import { EmployeeMatchCell } from "./EmployeeMatchCell";
import { EmployeeMatchSummary } from "./EmployeeMatchSummary";

const PREVIEW_GRID_HEIGHT = 420;

type GridRow = { id: number; [columnName: string]: string | number };

export type PreviewFileType = "shifts" | "template" | "nurses";

export type PreviewUploadButton = {
	label: string;
	// Receives the *filtered* CSV blob (attempts filter + sort applied) so callers can upload exactly what's previewed.
	onClick: (csvBlob: Blob) => void;
	loading?: boolean;
	disabled?: boolean;
};

type PreviewCardProps = {
	fileName: string;
	fileType: PreviewFileType;
	transformedData: ArrayBuffer;
	// Prefix for `data-testid` attributes on every interactive element in this card. Required so multiple
	// PreviewCards on the same page (e.g. AutoPromptSection's bulk view) don't collide.
	dataTestIdPrefix: string;
	// shifts/template only — drives the failed-tabs warning.
	failedTabs?: FailedTab[];
	onCancel: () => void;
	// Override the default download (e.g. to set a different filename); when omitted, downloads with `transformed_<fileName>.csv`.
	onDownload?: () => void;
	// Override the default download filename. Ignored when `onDownload` is set.
	downloadFilename?: string;
	// Action buttons under the preview. Orthogonal to `skip` — a card can show both.
	uploadButtons?: PreviewUploadButton[];
	// Renders the filename header plus the "Skip on upload" switch.
	skip?: boolean;
	onSkipChange?: (v: boolean) => void;
	// Bulk-mode: parent caches the filtered/sorted blob so Upload All uses the user's preview choices.
	onFilteredBlobChange?: (blob: Blob) => void;
};

// Used by the Data Load tab (uploadButtons and skip together) and by the Auto Prompt wizard (skip only).
export function PreviewCard({
	fileName,
	fileType,
	transformedData,
	dataTestIdPrefix,
	failedTabs = [],
	onCancel,
	onDownload,
	downloadFilename,
	uploadButtons,
	skip,
	onSkipChange,
	onFilteredBlobChange,
}: PreviewCardProps) {
	const {
		preview,
		filteredRows,
		hasAttemptsColumn,
		attemptsFilter,
		setAttemptsFilter,
		getCsvBlob,
		sortState,
		setSortState,
		sortingOptions,
		defaultSortState,
		hasEmployeeMatchColumn,
		employeeMatchCounts,
	} = useAttemptsFilter(transformedData, fileType === "nurses" ? undefined : fileType);

	// Send the latest filtered blob up when the filter changes.
	const onFilteredBlobChangeRef = useRef(onFilteredBlobChange);
	onFilteredBlobChangeRef.current = onFilteredBlobChange;
	// biome-ignore lint/correctness/useExhaustiveDependencies: getCsvBlob is not memoized; filteredRows tracks its content.
	useEffect(() => {
		if (!onFilteredBlobChangeRef.current) return;
		onFilteredBlobChangeRef.current(getCsvBlob());
	}, [filteredRows]);

	if (!preview) return null;

	const baseName = stripExtension(fileName, "file");
	const handleDownload = () => {
		if (onDownload) {
			onDownload();
			return;
		}
		downloadBlob(getCsvBlob(), downloadFilename ?? `transformed_${baseName}.csv`);
	};

	const isBulk = onSkipChange !== undefined;

	const gridRows: GridRow[] = filteredRows.map((cells, idx) => {
		const row: GridRow = { id: idx };
		preview.headers.forEach((h, i) => {
			row[h] = cells[i] ?? "";
		});
		return row;
	});
	const gridColumns: GridColDef<GridRow>[] = preview.headers.map((h) => {
		const column: GridColDef<GridRow> = { field: h, headerName: h, flex: 1, sortable: false };
		if (h !== EMPLOYEE_MATCH_COLUMN) return column;
		return {
			...column,
			// `display: "flex"` keeps a custom-rendered cell vertically centered like the plain-text ones.
			display: "flex",
			renderCell: (params: GridRenderCellParams<GridRow, string | number>) => (
				<EmployeeMatchCell value={String(params.value ?? "")} dataTestId={`${dataTestIdPrefix}-employee-match-cell-${params.row.id}`} />
			),
		};
	});

	return (
		<Paper variant="outlined" sx={{ p: 2 }}>
			{isBulk && (
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{fileName}
					</Typography>
					<FormControlLabel
						control={<Switch checked={Boolean(skip)} onChange={(e) => onSkipChange?.(e.target.checked)} data-testid={`${dataTestIdPrefix}-skip-toggle`} />}
						label="Skip on upload"
					/>
				</Box>
			)}

			{failedTabs.length > 0 && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
						{failedTabs.length} tab(s) failed to transform:
					</Typography>
					{failedTabs.map((tab) => (
						<Typography key={tab.name} variant="body2">
							<strong>{tab.name}</strong>: {tab.error}
						</Typography>
					))}
				</Alert>
			)}

			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
				<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					Preview ({filteredRows.length} rows
					{hasEmployeeMatchColumn && <EmployeeMatchSummary counts={employeeMatchCounts} dataTestId={`${dataTestIdPrefix}-employee-match-summary`} />})
				</Typography>

				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{hasAttemptsColumn && (
						<SystemDropdown<string>
							label="Filter by attempts"
							multiple
							items={[
								{ value: "consensus", label: "Consensus (all attempts)", selectedLabel: "Consensus" },
								{ value: "1", label: "Attempt 1" },
								{ value: "2", label: "Attempt 2" },
								{ value: "3", label: "Attempt 3" },
							]}
							selectedItems={attemptsFilter}
							onChangeMultiple={setAttemptsFilter}
							allOption="All"
							formControlSx={{ minWidth: 240 }}
							data-testid={`${dataTestIdPrefix}-attempts-filter`}
						/>
					)}
					{sortingOptions.length > 0 && (
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }} data-testid={`${dataTestIdPrefix}-sort`}>
							<Typography variant="body2" sx={{ color: "#4D5579" }}>
								Sort by
							</Typography>
							<SystemSortingButtonWithPopup<Row>
								sortingOptions={sortingOptions}
								initialState={sortState}
								setSortState={setSortState}
								defaultSortState={defaultSortState}
								iconOnly
							/>
						</Box>
					)}
				</Box>
			</Box>

			<Box sx={{ height: PREVIEW_GRID_HEIGHT, mb: 2 }} data-testid={`${dataTestIdPrefix}-grid`}>
				<SystemDataGrid<GridRow> rows={gridRows} columns={gridColumns} getRowId={(row) => row.id} dataTestId={`${dataTestIdPrefix}-grid`} />
			</Box>

			<Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>
				<SystemButton buttonVariant="secondary" onClick={onCancel} data-testid={`${dataTestIdPrefix}-cancel-button`}>
					Cancel
				</SystemButton>
				<SystemButton buttonVariant="secondary" onClick={handleDownload} data-testid={`${dataTestIdPrefix}-download-button`}>
					Download
				</SystemButton>
				{uploadButtons?.map((btn, i) => (
					<SystemButton
						key={btn.label}
						disabled={Boolean(btn.disabled)}
						loading={Boolean(btn.loading)}
						onClick={() => btn.onClick(getCsvBlob())}
						data-testid={`${dataTestIdPrefix}-upload-button-${i}`}
					>
						{btn.label}
					</SystemButton>
				))}
			</Box>
		</Paper>
	);
}
